import React from 'react';
import { Field, useField } from 'formik';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../lib/utils';

export const FormInput = ({
  name,
  label,
  type = 'text',
  leftIcon,
  rightIcon,
  placeholder,
  helperText,
  required = false,
  maxLength,
  showCharacterCount = false,
  disabled,
  className,
  ...props
}) => {
  const [field, meta] = useField(name);
  const hasError = meta.touched && meta.error;
  const [isFocused, setIsFocused] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);

  const inputType = type === 'password' && showPassword ? 'text' : type;
  const currentLength = field.value?.length || 0;
  const showCounter = showCharacterCount && maxLength;

  const shakeVariants = {
    shake: {
      x: [0, -8, 8, -8, 8, 0],
      transition: { duration: 0.4 },
    },
  };

  return (
    <div className={cn('w-full', className)}>
      {/* External Label */}
      {label && (
        <label
          htmlFor={name}
          className={cn(
            'block text-sm font-medium mb-1.5',
            hasError ? 'text-danger-600' : 'text-neutral-700'
          )}
        >
          {label}
          {required && <span className="text-danger-500 ml-0.5">*</span>}
        </label>
      )}

      {/* Input Container */}
      <motion.div
        animate={hasError && meta.touched ? 'shake' : ''}
        variants={shakeVariants}
      >
        <div className="relative">
          {/* Left Icon */}
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-neutral-400" aria-hidden="true">
              {React.cloneElement(leftIcon, { className: 'h-5 w-5' })}
            </div>
          )}

          {/* Input Field */}
          <Field
            {...field}
            {...props}
            id={name}
            type={inputType}
            disabled={disabled}
            placeholder={placeholder || ''}
            maxLength={maxLength}
            onFocus={(e) => {
              setIsFocused(true);
              props.onFocus?.(e);
            }}
            onBlur={(e) => {
              setIsFocused(false);
              field.onBlur(e);
              props.onBlur?.(e);
            }}
            className={cn(
              'w-full rounded-lg border py-2.5 text-sm transition-all duration-200',
              'placeholder:text-neutral-400',
              'focus:outline-none focus:ring-2 focus:ring-offset-0',
              leftIcon ? 'pl-10 pr-4' : 'px-4',
              (rightIcon || type === 'password') && 'pr-10',
              hasError
                ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500/20 bg-white'
                : isFocused
                ? 'border-primary-400 focus:border-primary-400 focus:ring-primary-300/30 bg-white'
                : 'border-neutral-300 hover:border-neutral-400 bg-white',
              disabled && 'bg-neutral-50 cursor-not-allowed opacity-60'
            )}
            aria-invalid={hasError}
            aria-describedby={hasError ? `${name}-error` : helperText ? `${name}-hint` : undefined}
            aria-required={required}
          />

          {/* Password Toggle */}
          {type === 'password' && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors focus:outline-none"
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          )}

          {/* Right Icon */}
          {rightIcon && type !== 'password' && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-neutral-400" aria-hidden="true">
              {React.cloneElement(rightIcon, { className: 'h-5 w-5' })}
            </div>
          )}
        </div>
      </motion.div>

      {/* Bottom Row */}
      <div className="flex items-start justify-between gap-2 mt-1">
        <div className="flex-1">
          <AnimatePresence mode="wait">
            {hasError ? (
              <motion.div
                key="error"
                initial={{ opacity: 0, y: -5, height: 0 }}
                animate={{ opacity: 1, y: 0, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                id={`${name}-error`}
                className="text-xs text-danger-600 font-medium"
                role="alert"
              >
                {meta.error}
              </motion.div>
            ) : helperText ? (
              <motion.p
                key="helper"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                id={`${name}-hint`}
                className="text-xs text-neutral-500"
              >
                {helperText}
              </motion.p>
            ) : null}
          </AnimatePresence>
        </div>

        {showCounter && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={cn(
              'text-xs font-medium transition-colors duration-200 flex-shrink-0',
              currentLength > maxLength * 0.9
                ? 'text-warning-600'
                : currentLength === maxLength
                ? 'text-danger-600'
                : 'text-neutral-500'
            )}
            aria-live="polite"
            aria-atomic="true"
          >
            {currentLength}/{maxLength}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default FormInput;
